/**
 * DID social-login identity (Phase A demo). Persisted so a page reload after
 * the callback keeps the user "signed in" for the demo. wallet_address here is
 * display-only — it carries no proof of ownership (sharp-edges S1).
 */
import { persist } from 'zustand/middleware';
import { create } from 'zustand';

export interface DidIdentity {
  sub: string;
  name?: string;
  picture?: string;
  walletAddress?: string;
}

interface DidAuthState {
  identity: DidIdentity | null;
  // Whether a real wallet (SIWE signer) is connected. DID login alone is
  // identity-only and NOT trade-ready until the user connects a wallet.
  walletConnected: boolean;
  // DID `sub`s for which the onboarding wizard was dismissed — keyed by sub so
  // a new DID identity (or logout) re-arms auto-open (spec #43 clarifications).
  onboardingDismissedFor: string[];
  /** DID social login: identity only, not trade-ready. */
  setIdentity: (identity: DidIdentity) => void;
  /** Wallet connect: identity + signer, trade-ready. */
  setWalletConnected: (identity: DidIdentity) => void;
  /** Mark onboarding dismissed for the current identity's sub. */
  dismissOnboarding: (sub: string) => void;
  clear: () => void;
}

export const useDidAuthStore = create<DidAuthState>()(
  persist(
    (set, get) => ({
      identity: null,
      walletConnected: false,
      onboardingDismissedFor: [],
      setIdentity: identity => set({ identity }),
      setWalletConnected: identity => set({ identity, walletConnected: true }),
      dismissOnboarding: sub => {
        const seen = get().onboardingDismissedFor;
        if (!seen.includes(sub)) set({ onboardingDismissedFor: [...seen, sub] });
      },
      clear: () => set({ identity: null, walletConnected: false }),
    }),
    { name: 'did-auth' }
  )
);
